import { AdvancedFiltersColumnConfig } from "@/views/Subjects/Overview/Overview/OverviewConfigTable";
import { ColumnVisibility } from "@/types/tableState.types";
import PaginatedTable from "@/components/UI/PaginatedTable";
import Loader from "@/components/UI/Loader";
import { decorateDashboardColumns } from "./DashboardTableHeaderAdapter";
import type { DashboardFilterMetadata } from "./DashboardTableHeader";
import { useDashboardTableSorting } from "@/hooks/useDashboardTableSorting";
import type { DashboardTableProps } from "./types";

export const DashboardTable = ({
  constants,
  currentTableState,
  data,
  loadingData,
  metadata,
  refreshingData = false,
  totalRecords,
  hasSelectedRecords,
  columnsTitleCreator,
  setFilters,
  setColumn,
  setSorting,
  setPagination,
  tableHeight,
  tableRef,
  rowKey,
  selectionIdentifier,
  rowIsSubject = false,
  extraColumns = [],
  onRow,
  rowSelection,
}: DashboardTableProps) => {
  const { pendingSorting, toggleSorting } = useDashboardTableSorting({
    filters: currentTableState.filters,
    sorting: currentTableState.sorting ?? [],
    setSorting,
  });

  const visibleColumns = currentTableState.columns.filter(
    (column) => column.visibility === ColumnVisibility.VISIBLE,
  );
  const filterMetadata = metadata.filters as unknown as Record<
    string,
    DashboardFilterMetadata
  >;
  const columns = decorateDashboardColumns(
    AdvancedFiltersColumnConfig(
      visibleColumns.map((column) => column.fieldName),
      currentTableState.lockedColumns,
      constants.idLink,
      columnsTitleCreator,
      // Without this the identifier cell never renders as a Link, so rows on
      // every dashboard screen (Trials, Sites, Investigators…) were unclickable.
      selectionIdentifier,
      rowIsSubject,
    ),
    {
      filterMetadata,
      currentFilters: currentTableState.filters,
      setFilters,
      sorting: currentTableState.sorting ?? [],
      pendingSorting,
      toggleSorting,
      columnsTitleCreator,
      dashboardColumns: currentTableState.columns,
      setDashboardColumns: setColumn,
    },
  );

  if (loadingData) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );
  }

  return (
    <div
      ref={tableRef}
      className="relative"
      aria-busy={refreshingData}
      inert={refreshingData || undefined}
    >
      <PaginatedTable
        className="shadow-md"
        columns={[...columns, ...extraColumns]}
        onRow={onRow}
        rowSelection={rowSelection}
        setPagination={setPagination}
        data={data?.results}
        tableHeight={tableHeight}
        rowKey={rowKey}
        pagination={currentTableState.pagination}
        footer={() => (
          <div className="flex gap-4">
            <p className="font-bold" data-testid="table-total-records">
              Total records: {totalRecords}
            </p>
            {hasSelectedRecords && (
              <p className="font-bold" data-testid="table-selected-records">
                Selected records: {data?.count}
              </p>
            )}
          </div>
        )}
      />
      {refreshingData && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-white/60"
          aria-label="Loading table data"
          role="status"
        >
          <Loader noMargin width="64px" height="64px" />
        </div>
      )}
    </div>
  );
};
